import { useCallback, useEffect } from 'react'
import { ScrollView, StyleSheet } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import MagnetCrossSellingCard from '@zet/common/components/statusCard/magnetCrossSellingCard'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { colors, Icon, SH1, Stack, XStack } from '@zet/design-system'
import { APP_NAME } from '@zet/end-points'
import ProductCard from '@zet/feature-brands/components/productCard'
import { useLeadCrossSelling } from '@zet/feature-leads/api/leadDetails/hooks'
import { i18n } from '@zet/localization'

import LeadCrossSellingRecommendationLoader from './leadCrossSellingRecommendationLoader'

interface LeadCrossSellingProps {
  leadId: number
  customerId: number
}

const PAGE = 1
const LIMIT = 5

const LeadCrossSelling = ({ leadId, customerId }: LeadCrossSellingProps) => {
  const route = useRoute()
  const { data, isSuccess, shouldRefetch } = useLeadCrossSelling(
    leadId,
    PAGE,
    LIMIT
  )

  const crossSellingRecommendations = data?.pages.flatMap(
    (page) => page.crossSellingRecommendations
  )

  useEffect(() => {
    if (isSuccess && crossSellingRecommendations?.length) {
      crossSellingScreenViewEvent()
    }
  }, [isSuccess, crossSellingRecommendations?.length])

  const crossSellingScreenViewEvent = useCallback(() => {
    publishEvent('screen_view', {
      item_name: 'rejected_cross_selling_recommendations',
      brand_id: crossSellingRecommendations?.[0]?.partnerId,
      customer_id: customerId,
    })
  }, [isSuccess, crossSellingRecommendations?.length])

  if (shouldRefetch) {
    return <LeadCrossSellingRecommendationLoader />
  }

  if (crossSellingRecommendations && crossSellingRecommendations?.length > 0) {
    return (
      <Stack className="mt-2 bg-white">
        <XStack className="items-center px-3 py-3">
          <Icon type="Star" size={24} color={colors.decorative.bee[100]} />
          <SH1>
            {i18n.t('leads.eligibleBrands', {
              count: crossSellingRecommendations?.length ?? 0,
            })}
          </SH1>
        </XStack>
        <ScrollView
          showsHorizontalScrollIndicator
          horizontal
          contentContainerStyle={styles.scrollView}
        >
          {crossSellingRecommendations?.map((data, index) => {
            if (data?.showMagnetCrossSelling && APP_NAME !== MY_TEAMS_ADVISOR) {
              return (
                <MagnetCrossSellingCard
                  customerName={data?.customerName}
                  partnerId={data?.partnerId}
                  customerId={customerId}
                  isRecommendationFlow={true}
                  key={data?.partnerId}
                  isCardOnCard={data?.isCardOnCard}
                  dynamicRejectionPayout={data?.dynamicRejectionPayout}
                  approvalRate={data?.approvalRate}
                  approvalRateTextColor={data?.approvalRateTextColor}
                  index={index + 1}
                  leadSource="magnetCrossSellingLeadPage"
                  isLeadPageCrossSelling
                  route={route}
                />
              )
            }
            return (
              <Stack className="flex-1">
                <ProductCard
                  partnerId={data?.partnerId}
                  customerId={customerId}
                  isRecommendationFlow={true}
                  key={data?.partnerId}
                  isCardOnCard={data?.isCardOnCard}
                  dynamicRejectionPayout={data?.dynamicRejectionPayout}
                  approvalRate={data?.approvalRate}
                  approvalRateTextColor={data?.approvalRateTextColor}
                  index={index + 1}
                  leadSource="crossSellingLeadPage"
                  isLeadPageCrossSelling
                  route={route}
                />
              </Stack>
            )
          })}
        </ScrollView>
      </Stack>
    )
  }
  return null
}

export default LeadCrossSelling

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
})
